(function (): void {
    'use strict';

    class FormDoubleSubmitPrevention {
        private form: HTMLFormElement;
        private isSubmitting: boolean = false;
        private readonly HIDDEN_INPUT_CLASS = 'js-double-submit-prevention-hidden';
        private static pageShowListenerInitialized = false;
        private static instances: FormDoubleSubmitPrevention[] = [];

        constructor(form: HTMLFormElement) {
            this.form = form;
            this.initializeEventListeners();
            FormDoubleSubmitPrevention.instances.push(this);
        }

        private initializeEventListeners(): void {
            this.form.addEventListener('submit', (event) => this.handleSubmit(event));

            // Register pageshow listener only once for all forms
            if (!FormDoubleSubmitPrevention.pageShowListenerInitialized) {
                window.addEventListener('pageshow', (event) => FormDoubleSubmitPrevention.handlePageShowAll(event));
                FormDoubleSubmitPrevention.pageShowListenerInitialized = true;
            }
        }

        private static handlePageShowAll(event: PageTransitionEvent): void {
            if (event.persisted) {
                FormDoubleSubmitPrevention.instances.forEach((instance) => instance.resetForm());
            }
        }

        private handleSubmit(event: SubmitEvent): void {
            if (this.isSubmitting) {
                event.preventDefault();
                console.log('Form submission already in progress. Please wait...');
                return;
            }

            this.isSubmitting = true;
            this.preserveClickedButtonValue(event.submitter);
            this.disableSubmitButtons();

            // A later submit listener (e.g. a client-side reCAPTCHA or validation guard) may
            // cancel the submission. This listener runs first and has already disabled the
            // buttons, so a prevented (non-navigating) submit would otherwise leave the form
            // permanently locked. Detect that after the event and roll the lock back.
            window.setTimeout(() => {
                if (event.defaultPrevented) {
                    this.resetForm();
                }
            }, 0);
        }

        private preserveClickedButtonValue(submitter: EventTarget | null): void {
            const clickedButton = submitter as HTMLButtonElement | HTMLInputElement | null;

            if (clickedButton && clickedButton.name) {
                const hiddenInput = document.createElement('input');
                hiddenInput.type = 'hidden';
                hiddenInput.name = clickedButton.name;
                hiddenInput.value = clickedButton.value || '';
                hiddenInput.className = this.HIDDEN_INPUT_CLASS;
                this.form.appendChild(hiddenInput);
            }
        }

        private disableSubmitButtons(): void {
            const submitButtons = this.getSubmitButtons();
            submitButtons.forEach((button) => {
                button.disabled = true;
            });
        }

        private enableSubmitButtons(): void {
            const submitButtons = this.getSubmitButtons();
            submitButtons.forEach((button) => {
                button.disabled = false;
            });
        }

        private getSubmitButtons(): NodeListOf<HTMLButtonElement | HTMLInputElement> {
            return this.form.querySelectorAll('button[type="submit"], input[type="submit"]');
        }

        private removeHiddenInputs(): void {
            const hiddenInputs = this.form.querySelectorAll<HTMLInputElement>(`.${this.HIDDEN_INPUT_CLASS}`);
            hiddenInputs.forEach((input) => input.remove());
        }

        private resetForm(): void {
            this.isSubmitting = false;
            this.enableSubmitButtons();
            this.removeHiddenInputs();
        }
    }

    function initDoubleSubmitPrevention(): void {
        const forms = document.querySelectorAll<HTMLFormElement>('form');
        forms.forEach((form) => new FormDoubleSubmitPrevention(form));
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initDoubleSubmitPrevention);
    } else {
        initDoubleSubmitPrevention();
    }
})();
